import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  safeNavigate: (path: string) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(
  null
);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const safeNavigate = useCallback(
    (path: string) => {
      if (hasUnsavedChanges) {
        setPendingPath(path);
        setShowWarning(true);
      } else {
        navigate(path);
      }
    },
    [hasUnsavedChanges, navigate]
  );

  const handleStayOnPage = () => {
    setShowWarning(false);
    setPendingPath(null);
  };

  const handleLeavePage = () => {
    setShowWarning(false);
    setHasUnsavedChanges(false);
    if (pendingPath) {
      navigate(pendingPath);
    }
    setPendingPath(null);
  };

  return (
    <UnsavedChangesContext.Provider
      value={{ hasUnsavedChanges, setHasUnsavedChanges, safeNavigate }}
    >
      {children}

      {/* Navigation Warning Dialog */}
      <Dialog
        open={showWarning}
        onClose={handleStayOnPage}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 400,
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          {/* Close button */}
          <IconButton
            onClick={handleStayOnPage}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Title with Warning Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <WarningIcon sx={{ fontSize: 28, color: "warning.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Unsaved Changes
            </Typography>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You have unsaved changes that will be lost if you leave this page.
            Are you sure you want to continue?
          </Typography>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            <Button
              onClick={handleStayOnPage}
              variant="outlined"
              sx={{
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Stay on Page
            </Button>
            <Button
              onClick={handleLeavePage}
              variant="contained"
              color="error"
              sx={{
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Discard Changes
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider"
    );
  }
  return context;
}
